import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ListItem, UnorderedList } from '@chakra-ui/react';

import Accordion from 'components/_core/Accordion';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { UpdateTaxDataDocument } from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';

import StepLayout from '../../layout/Step';
import { StepComponentProps } from '../types';
import { locales } from './locales';

enum IsSubjectToVat {
  YES = 'YES',
  NO = 'NO',
}

type SavePayload = { isSubjectToVat: boolean };

const Vat: FC<StepComponentProps<SavePayload>> = ({
  context,
  onBack,
  onNext,
  onSave,
}) => {
  const { showToast } = useToast();
  const { companyProfile, stepProgress, taxForecastProfile } = context;

  const [selectedValue, setSelectedValue] = useState(
    Boolean(taxForecastProfile?.isSubjectToVat),
  );

  const [updateTaxData, { loading }] = useMutation(UpdateTaxDataDocument, {
    onCompleted: () => {
      onSave({ isSubjectToVat: selectedValue });
      onNext();
    },
    onError: () => showToast({ message: locales.error, type: 'error' }),
    variables: {
      input: {
        companyProfileId: companyProfile.companyProfileId,
        taxForecastProfileData: { isSubjectToVat: selectedValue },
        taxFrequency: companyProfile.taxFrequency,
      },
    },
  });

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body>
        <Heading marginBottom="space-32">{locales.title}</Heading>

        <Form.Radio.Group
          direction="row"
          onChange={(newValue: IsSubjectToVat) =>
            setSelectedValue(newValue === IsSubjectToVat.YES ? true : false)
          }
          radioPosition="right"
          value={selectedValue ? IsSubjectToVat.YES : IsSubjectToVat.NO}
        >
          <Form.Radio value={IsSubjectToVat.YES} variant="outline">
            <Form.Radio.Title>{locales.yes}</Form.Radio.Title>
          </Form.Radio>

          <Form.Radio value={IsSubjectToVat.NO} variant="outline">
            <Form.Radio.Title>{locales.no}</Form.Radio.Title>
          </Form.Radio>
        </Form.Radio.Group>
      </StepLayout.Body>

      <StepLayout.Sidebar>
        <Heading marginBottom="space-16">{locales.sidebar.title}</Heading>

        <Accordion allowMultiple allowToggle={undefined}>
          <Accordion.Item title={locales.sidebar.basicVatExemption.question}>
            <Accordion.Text>
              {locales.sidebar.basicVatExemption.info}

              <UnorderedList>
                <ListItem>
                  <Text variant="light">
                    {locales.sidebar.basicVatExemption.bullets[0]}
                  </Text>
                </ListItem>

                <ListItem>
                  <Text variant="light">
                    {locales.sidebar.basicVatExemption.bullets[1]}
                  </Text>
                </ListItem>
              </UnorderedList>
            </Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.liableAsMicro.question}>
            <Accordion.Text>
              {locales.sidebar.liableAsMicro.info}
            </Accordion.Text>
          </Accordion.Item>
        </Accordion>
      </StepLayout.Sidebar>

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isLoading={loading}
          onClick={() => updateTaxData()}
        />
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default Vat;
